import { Injectable, Output, EventEmitter } from '@angular/core';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';

export class LoadingMessage {
  id: number = 0;
  text: string = '';
  // Optional progress fraction (0..1). Undefined = indeterminate spinner.
  // Set / updated via LoadingService.setProgress(id, fraction, sub?).
  progress?: number;
  // Optional sub-text rendered under the main message (e.g. "12,403 / 17,258 users").
  sub?: string;
  // Cosmetic-only: distinct id used by the overlay's *ngFor trackBy.
  // Numbers from Date.now() can collide if two adds fire in the same ms
  // (rare with HOSTILE setIntervals); a monotonic suffix keeps each row
  // unique without growing the public surface.
  static _counter = 0;

  constructor(text: string) {
    this.id = Date.now() * 1000 + (++LoadingMessage._counter % 1000);
    this.text = text;
  }
}

@Injectable({
  providedIn: 'root'
})
export class LoadingService {

  private _visible = new BehaviorSubject<boolean>(false);
  public visible$ = this._visible.asObservable();

  // Bumped on every progress/sub update so the overlay's CD pass
  // re-renders. The messages array is mutated in place, which Angular
  // doesn't see through OnPush — emitting on a Subject is the cheap fix.
  private _tick = new BehaviorSubject<number>(0);
  public tick$ = this._tick.asObservable();

  messages = [] as LoadingMessage[];

  constructor() {}

  add(message: string) {
    let newMsg = new LoadingMessage(message);
    this.messages.push(newMsg);
    this._visible.next(true);
    return newMsg.id;
  }

  remove(id: number) {
    if (!id) return;
    _.remove(this.messages, (msg) => msg.id === id);
    this._visible.next(this.messages.length > 0);
  }

  // Update a message's progress fraction + optional sub-line. Long-running
  // operations (cache_cognito_users polls, multi-file uploads, batch
  // updates) call this after add() to drive the progress bar in the
  // overlay. Passing undefined / null clears the bar back to indeterminate.
  setProgress(id: number, progress: number | null | undefined, sub?: string) {
    const m = this.messages.find((x) => x.id === id);
    if (!m) return;
    if (progress === null || progress === undefined) {
      m.progress = undefined;
    } else {
      // Clamp to [0,1] so a runaway divisor (prev_total stale, etc.)
      // can't push the bar past 100%.
      m.progress = Math.max(0, Math.min(1, progress));
    }
    if (sub !== undefined) m.sub = sub;
    this._tick.next(this._tick.value + 1);
  }
}
